import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Row } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext, useToast, useTranslator } from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { useCameraCapture } from '../hooks';
import { withForcedAiError } from '../helpers';
import {
  AddUserProductFormValues,
  SCAN_LABEL_RESULT_EVENT,
  SCAN_LABEL_RESULT_STORAGE_KEY,
} from '../AddUserProduct/helpers';

const INTRO_SEEN_KEY = 'tracker_scan_label_intro_seen';

type ScanStep = 'front' | 'nutrition' | 'analyzing';

interface TrackerScanLabelContainerProps {
  scanLabel: (images: File[], userComment?: string) => Promise<unknown>;
  cancelScanLabel: () => void;
}

const TrackerScanLabelContainer: React.FC<TrackerScanLabelContainerProps> = ({
  scanLabel,
  cancelScanLabel,
}) => {
  const { t, language } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    formValues?: AddUserProductFormValues;
    fromAutoScan?: boolean;
  } | null;
  const formValues = locationState?.formValues;
  // When launched straight from the action sheet (autoScan), an empty add-product form sits
  // between the tracker and this scanner. Closing should skip it and return to the tracker; when
  // opened from an actual add-product form, closing returns to that form.
  const fromAutoScan = locationState?.fromAutoScan ?? false;
  const { toastError } = useToast();

  const {
    images: {
      cameraShutter,
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      photoGalleryIcon,
      videoPoster,
      scanLabelTipsImgEn,
      scanLabelTipsImgPl,
      scanInfoIcon,
      aiStarLightIcon,
      doneIcon,
    },
  } = useAppContext();

  const camera = useCameraCapture();
  const [step, setStep] = useState<ScanStep>('front');
  // Intro bottom sheet ("Take 2 photos"): shown automatically on the first ever visit and
  // re-openable any time via the "i" button — same content in both cases.
  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem(INTRO_SEEN_KEY));
  const [frontThumbUrl, setFrontThumbUrl] = useState<string | null>(null);
  const [nutritionThumbUrl, setNutritionThumbUrl] = useState<string | null>(null);
  const [scanError, setScanError] = useState<
    'noFood' | 'rateLimit' | 'aiAccessDenied' | 'generic' | null
  >(null);
  const frontPhotoRef = useRef<File | null>(null);
  // Kept so a failed analysis can be retried with the very same pair of photos ("Analyze again")
  // instead of forcing the user to shoot both again.
  const nutritionPhotoRef = useRef<File | null>(null);
  const abandonedRef = useRef(false);
  const scanAreaRef = useRef<HTMLDivElement | null>(null);
  // Mirrors of the two thumb URLs so the route-cleanup below can revoke whatever is current without
  // taking them as effect dependencies (which would re-run the camera lifecycle on every shot).
  const thumbUrlsRef = useRef<{ front: string | null; nutrition: string | null }>({
    front: null,
    nutrition: null,
  });
  thumbUrlsRef.current = { front: frontThumbUrl, nutrition: nutritionThumbUrl };

  // Camera lifecycle — tied to this being the visible route, NOT to mount/unmount:
  // SlideRoutes renders with destroy={false}, so this component stays mounted after
  // navigating away (back gesture, TitleBar back, successful scan). A mount-effect
  // cleanup would never run and the device camera would stay on. `useLocation` returns
  // the live router location even for kept-mounted pages, so it tells us when we leave.
  const isActiveRoute = location.pathname.endsWith('/scan-label');
  useEffect(() => {
    if (!isActiveRoute) return undefined;
    let active = true;
    const init = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (active) await camera.startCamera();
    };
    init();
    const stop = () => camera.stopCamera();
    // Browser back/forward: SlideRoutes (destroy={false}) keeps this component mounted with a
    // FROZEN router location, so `isActiveRoute` stays true and the cleanup below never runs on
    // back — the camera would keep streaming. popstate fires on real browser navigation and
    // exposes the live URL via window.location, so release the camera whenever we're no longer
    // on the scan-label route.
    const handlePopState = () => {
      if (!window.location.pathname.endsWith('/scan-label')) {
        camera.stopCamera();
      }
    };
    window.addEventListener('beforeunload', stop);
    window.addEventListener('pagehide', stop);
    window.addEventListener('popstate', handlePopState);
    return () => {
      active = false;
      camera.stopCamera();
      window.removeEventListener('beforeunload', stop);
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('popstate', handlePopState);
      // Reset the scan state so a later re-entry (component may still be mounted)
      // starts from a fresh step 1/2 instead of a stale 2/2 with old thumbs. This is the only
      // place the thumbs are released other than being replaced by a new shot: leaving the route
      // is the point at which nothing can still be displaying them.
      if (thumbUrlsRef.current.front) URL.revokeObjectURL(thumbUrlsRef.current.front);
      if (thumbUrlsRef.current.nutrition) URL.revokeObjectURL(thumbUrlsRef.current.nutrition);
      frontPhotoRef.current = null;
      nutritionPhotoRef.current = null;
      setStep('front');
      setScanError(null);
      setFrontThumbUrl(null);
      setNutritionThumbUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRoute]);

  // Granting camera access from the browser's own settings leaves the "no access" alert on screen
  // next to a live preview: the lifecycle effect above runs once per route entry, so nothing calls
  // startCamera() again and the hook's error (only cleared on startCamera) goes stale. Retry when
  // the permission flips to granted, and — because Safari does not expose 'camera' to the
  // Permissions API — also when the tab regains focus, which is when the user comes back from the
  // settings UI. The retry is a no-op unless we are actually holding an error.
  useEffect(() => {
    if (!isActiveRoute) return undefined;

    const retryIfBlocked = () => {
      if (!camera.error) return;
      camera.startCamera();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') retryIfBlocked();
    };

    window.addEventListener('focus', retryIfBlocked);
    document.addEventListener('visibilitychange', handleVisibility);

    // Chrome/Edge/Firefox report camera permission changes live, so recovery happens the moment
    // the user flips the toggle without waiting for a tab switch.
    let permissionStatus: PermissionStatus | null = null;
    const onPermissionChange = () => {
      if (permissionStatus?.state === 'granted') retryIfBlocked();
    };
    navigator.permissions
      ?.query({ name: 'camera' as PermissionName })
      .then((status) => {
        permissionStatus = status;
        status.addEventListener('change', onPermissionChange);
      })
      // Safari rejects on the unsupported 'camera' name — the listeners above still cover it.
      .catch(() => undefined);

    return () => {
      window.removeEventListener('focus', retryIfBlocked);
      document.removeEventListener('visibilitychange', handleVisibility);
      permissionStatus?.removeEventListener('change', onPermissionChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRoute, camera.error]);

  // Blob URLs are revoked explicitly — when replaced by a new shot and when the route is left (see
  // the lifecycle effect above). Deliberately NOT in a `[thumbUrl]`-keyed cleanup: that also fires
  // whenever the component re-renders its way out of a kept-mounted state (SlideRoutes
  // destroy={false}), which revoked the very blob still sitting in an <img src> and left grey
  // placeholders behind.

  const dismissIntro = () => {
    localStorage.setItem(INTRO_SEEN_KEY, 'true');
    setShowIntro(false);
  };

  const openIntro = () => setShowIntro(true);

  // The tips image carries baked-in label text, so it has to follow the UI language. Only PL is
  // localised so far; every other language falls back to EN. Same shape as DietType's dietImg().
  const tipsImg = (() => {
    switch (language) {
      case 'pl-PL':
        return scanLabelTipsImgPl;
      default:
        return scanLabelTipsImgEn;
    }
  })();

  // A technical failure shows the app-wide red toast (Figma 9767:65957 puts it across the top),
  // unlike the content-level failures which render as a card under the photos. The error state is
  // still set so the screen offers Retake photos / Analyze again after the toast fades.
  const failGeneric = () => {
    setScanError('generic');
    toastError();
  };

  const analyze = async (front: File, nutrition: File) => {
    setStep('analyzing');
    await camera.stopCamera();
    // The TitleBar back button (and browser back) leaves this screen without aborting the
    // request; when the response lands after the user already navigated away, acting on it
    // would navigate(-1) a second time and pop the form off the history stack. SlideRoutes
    // keeps this component mounted (destroy={false}), so track leaving via popstate.
    abandonedRef.current = false;
    const markAbandoned = () => {
      abandonedRef.current = true;
    };
    window.addEventListener('popstate', markAbandoned);
    try {
      // withForcedAiError passes the real response through untouched unless developer options
      // pin the flow to a chosen failure (dev/stage only).
      const response: any = withForcedAiError(await scanLabel([front, nutrition]));
      if (abandonedRef.current) return;
      if (response?.__typename === 'AnalyzedDailyTrackerIngredients') {
        const payload = {
          primaryIngredient: response.primaryIngredient,
          ...(response.requestId && { requestId: response.requestId }),
          ...(formValues && { formValues }),
          updatedAt: Date.now(),
        };
        sessionStorage.setItem(SCAN_LABEL_RESULT_STORAGE_KEY, JSON.stringify(payload));
        window.dispatchEvent(new CustomEvent(SCAN_LABEL_RESULT_EVENT, { detail: payload }));
        // autoScan flow entered the scanner straight from the tracker, so there's no add-product
        // form behind it — open one (it reads the stored result on mount and prefills). The form
        // flow already has the form behind it, so just go back to it (it consumes the CustomEvent).
        if (fromAutoScan) {
          // No meal is being edited on this path, so saving the product must open a fresh meal
          // holding it (same as the standalone barcode flow) rather than dropping the user back on
          // this scanner.
          navigate(location.pathname.replace(/\/scan-label$/, ''), {
            state: { createMealFromProduct: true },
          });
        } else {
          navigate(-1);
        }
      } else if (response?.__typename === 'NotFoodError') {
        setScanError('noFood');
      } else if (response?.__typename === 'RateLimitExceeded') {
        setScanError('rateLimit');
      } else if (response?.__typename === 'AiAccessDenied') {
        setScanError('aiAccessDenied');
      } else {
        failGeneric();
      }
    } catch {
      // A cancel (Cancel button / back navigation aborts the request) must not surface as an
      // error on this — by then hidden — screen.
      if (!abandonedRef.current) failGeneric();
    } finally {
      window.removeEventListener('popstate', markAbandoned);
    }
  };

  const handleCapture = async (file: File) => {
    if (step === 'front') {
      frontPhotoRef.current = file;
      setFrontThumbUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(file);
      });
      setStep('nutrition');
    } else if (step === 'nutrition' && frontPhotoRef.current) {
      nutritionPhotoRef.current = file;
      setNutritionThumbUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(file);
      });
      await analyze(frontPhotoRef.current, file);
    }
  };

  const handleShutter = async () => {
    if (camera.isCapturing || step === 'analyzing') return;
    try {
      // Crop exactly to the white scan frame the user aims with, at full source resolution
      // (no downscaling) and high JPEG quality — the nutrition table fills the sent image,
      // which is what the model actually needs to read small print.
      const file = await camera.capturePhoto({
        crop: 'element',
        cropElement: scanAreaRef.current,
        quality: 0.95,
        fileName: step === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
      });
      await handleCapture(file);
    } catch {
      toastError();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Gallery uploads have no scan frame to crop to — send the whole photo, but keep it under
    // the backend's 1MB-per-image limit (1600px + q0.92 instead of the legacy 1024px + q0.9).
    const file = await camera.processUploadedFile(event, {
      crop: 'scale',
      maxDimension: 1600,
      quality: 0.92,
      fileName: step === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
    });
    if (file) await handleCapture(file);
  };

  // "Retake photos" — discard both shots and go back to step 1/2 with the camera live.
  const handleRetry = async () => {
    setScanError(null);
    frontPhotoRef.current = null;
    nutritionPhotoRef.current = null;
    if (frontThumbUrl) URL.revokeObjectURL(frontThumbUrl);
    setFrontThumbUrl(null);
    if (nutritionThumbUrl) URL.revokeObjectURL(nutritionThumbUrl);
    setNutritionThumbUrl(null);
    setStep('front');
    await camera.startCamera();
  };

  // "Analyze again" — resend the photos already taken. Only offered when both are still held;
  // a camera-permission failure happens before any shot exists.
  const handleAnalyzeAgain = async () => {
    if (!frontPhotoRef.current || !nutritionPhotoRef.current) return;
    setScanError(null);
    await analyze(frontPhotoRef.current, nutritionPhotoRef.current);
  };

  const handleClose = async () => {
    abandonedRef.current = true;
    cancelScanLabel();
    await camera.stopCamera();
    // The scanner is entered directly (no add-product launcher in between) in BOTH flows now, so a
    // single back step is correct: autoScan came straight from the tracker, and the form flow came
    // from the add-product form. navigate(-1) also keeps the SlideRoutes slide-out animation.
    navigate(-1);
  };

  // --- render ---
  // analyzing state — Figma "scanner.label.analyzing.withPhotos" (8839:43741): white page with
  // navbar, the voice-log animated circle, duration hint, both captured photos and Cancel. The
  // same screen carries every failure (Figma 9767:65957): the photos stay on screen under a
  // dismissible error toast, with "Retake photos" / "Analyze again" replacing Cancel, so a failed
  // analysis no longer throws away work the user already did.
  if (step === 'analyzing') {
    return (
      <Layout>
        <TitleBar showBackBtn={true}>{t('tracker.scanLabel.navbarTitle')}</TitleBar>

        <div className="page-template-tracker edit-meal-page">
          <Container className="py-md-4 full-height d-flex flex-column">
            <div className="tracker-container text-center d-flex flex-column flex-grow-1">
              {/* While analysing: the rotating gradient with the white disc and the status text in
                  it. On failure the disc goes away and only the still gradient remains (Figma
                  9767:65957) — an empty white circle read as a stuck loader. */}
              <div
                className={`voice-button ${scanError ? '' : 'is-submitting'}`}
                data-testid="scan-label-analyzing"
              >
                {!scanError && (
                  <div className="loader-container">
                    <p className="mb-0 medium">{t('tracker.scanLabel.loader.analyzing')}</p>
                  </div>
                )}
              </div>

              {!scanError && (
                <p className="description text-text-variant-color">
                  {t('tracker.scanner.analyzing.duration')}
                </p>
              )}

              {/* mt-l/mb-l, not my-l: this project has no .my-l utility (only .my-m / .my-xl), so
                  the my-l that used to be here silently did nothing. */}
              {frontThumbUrl && (
                <div className="d-flex justify-content-center gap-m mt-l mb-l">
                  <div className="captured-photo-item">
                    <div className="captured-photo captured-photo-lg">
                      <img src={frontThumbUrl} alt="" />
                      <img src={doneIcon} alt="" className="captured-photo-badge" />
                    </div>
                    <p className="description mb-0">
                      {t('tracker.scanner.analyzing.imageLabel.front')}
                    </p>
                  </div>
                  {nutritionThumbUrl && (
                    <div className="captured-photo-item">
                      <div className="captured-photo captured-photo-lg">
                        <img src={nutritionThumbUrl} alt="" />
                        <img src={doneIcon} alt="" className="captured-photo-badge" />
                      </div>
                      <p className="description mb-0">
                        {t('tracker.scanner.analyzing.imageLabel.nutrition')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Content-level failures sit in the scroll area at the end of the content, the same
                  placement DescribeMeal uses for its noFood/rateLimit alerts — not glued to the
                  footer buttons. `generic` is not handled here: a technical failure surfaces as the
                  app-wide red toast. */}
              {(scanError === 'noFood' ||
                scanError === 'rateLimit' ||
                scanError === 'aiAccessDenied') && (
                <Alert
                  // Warning (amber icon) for "nothing to analyze", danger (red icon) for the AI
                  // limit and access denial — both render as the DS white toast card.
                  color={scanError === 'noFood' ? 'warning' : 'danger'}
                  className="text-start mt-xl mb-0"
                  fade={false}
                  data-testid="scan-label-error"
                >
                  <p className="mb-0">
                    {scanError === 'noFood' && t('tracker.scanner.noLabel.notification')}
                    {scanError === 'rateLimit' && t('tracker.limitUsageError.notification')}
                    {scanError === 'aiAccessDenied' &&
                      t('tracker.scanner.aiAccessDenied.notification')}
                  </p>
                </Alert>
              )}
            </div>
          </Container>

          {/* Pinned to the bottom of the viewport, matching the voice-analysis screen — inside the
              scroll container the actions sat right under the photos with barely any gap. */}
          <div className="sticky-footer">
            <Container className="px-3">
              <Row>
                <Col xs="12" md={{ size: 4, offset: 4 }}>
                  {scanError ? (
                    <>
                      <Button
                        color="link"
                        className="w-100 mb-s"
                        onClick={handleRetry}
                        data-testid="scan-label-retake"
                      >
                        {t('tracker.scanLabel.button.retakePhoto')}
                      </Button>
                      {/* Hidden when the failure happened before both photos existed (e.g. camera
                          permission) — there would be nothing to resend. */}
                      {frontPhotoRef.current && nutritionPhotoRef.current && (
                        <Button
                          color="primary"
                          className="w-100"
                          onClick={handleAnalyzeAgain}
                          data-testid="scan-label-analyze-again"
                        >
                          {/* Same white AI star the other primary "analyze" buttons use
                              (DescribeMeal / DescribeActivity), not the coloured AI badge pill —
                              Figma 9771:11064 places a 24x24 button icon here. */}
                          <img
                            src={aiStarLightIcon}
                            alt=""
                            style={{ marginTop: '-3px', marginBottom: '-3px' }}
                            className="me-2"
                          />
                          {t('tracker.scanLabel.button.analyze')}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button
                      color="secondary"
                      outline
                      className="w-100"
                      onClick={handleClose}
                      data-testid="scan-label-cancel"
                    >
                      {/* Shared with the add-product form: scan label is a step of that flow and
                          both buttons mean the same "abandon and go back". */}
                      {t('tracker.addProduct.cancelButton')}
                    </Button>
                  )}
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <div className="scan-plate">
      <video
        ref={camera.videoRef}
        className="camera-video-fullscreen"
        poster={videoPoster}
        playsInline
        muted
        style={{ opacity: camera.isVideoVisible ? 1 : 0 }}
      />
      <canvas ref={camera.canvasRef} className="d-none" />
      <input
        ref={camera.fileInputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFileUpload}
      />

      <div className="camera-overlay-fullscreen">
        {camera.isCameraActive && <div className="scan-area" ref={scanAreaRef} />}

        {frontThumbUrl && step === 'nutrition' && !scanError && !camera.error && (
          <div
            className="captured-photo captured-photo-anchored"
            data-testid="scan-label-front-thumb"
          >
            <img src={frontThumbUrl} alt="" />
            <img src={doneIcon} alt="" className="captured-photo-badge" />
          </div>
        )}

        <div className="top-controls">
          <button
            className="scan-plate-button"
            onClick={handleClose}
            data-testid="scan-label-close"
          >
            <img src={closeWhiteIcon} alt="" />
          </button>
          <p className="mb-0 scan-steps-pill">
            <span className="semi-bold">
              {t('tracker.scanner.steps.current', {
                current: step === 'front' ? 1 : 2,
                total: 2,
              })}
            </span>{' '}
            {/* Graphic separator, not copy — kept out of the locale files for the same reason the
                tick mark was: there is nothing to translate. */}
            ·{' '}
            <span>
              {step === 'front'
                ? t('tracker.scanner.steps.frontOfProduct')
                : t('tracker.scanner.steps.nutritionLabel')}
            </span>
          </p>
          <button
            className="scan-plate-button"
            onClick={openIntro}
            data-testid="scan-label-info"
            aria-label={t('tracker.scanner.intro.title')}
          >
            <img src={scanInfoIcon} alt="" />
          </button>
        </div>

        <div className="bottom-controls">
          {/* Analysis failures are not handled here: they always carry step === 'analyzing', so the
              earlier return renders them on the analyzing screen with the photos still visible. */}
          {camera.error ? (
            <div className="text-center w-100">
              <Alert color="danger" className="mb-0" fade={false}>
                <p className="mb-0 text-text-main-color text-start">
                  {t('tracker.scanFood.noAccessCamera.notification')}
                </p>
              </Alert>
              {/* No gallery button here on purpose: without camera permission the browser file
                  picker is the only way in, and offering it next to the "no access" alert reads as
                  a working fallback. Scan food (ScanPlate) hides it the same way. */}
              {!camera.isCameraActive && !camera.isSearchingCamera && (
                <Button
                  color="link"
                  size="sm"
                  onClick={() => window.location.reload()}
                  disabled={camera.isSearchingCamera || camera.isCapturing}
                >
                  <p>{t('tracker.scan.button.refreshCamera')}</p>
                </Button>
              )}
            </div>
          ) : (
            <>
              {camera.isTorchSupported ? (
                <button className="scan-plate-button" onClick={camera.toggleTorch}>
                  <img src={camera.isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                </button>
              ) : (
                <div style={{ width: 40, height: 40 }} />
              )}
              <button
                className="scan-plate-button"
                onClick={handleShutter}
                disabled={camera.isCapturing}
                data-testid="scan-label-shutter"
                data-test-id="scan_label_shutter"
              >
                <img src={cameraShutter} alt="" />
              </button>
              <button className="scan-plate-button" onClick={camera.openGallery}>
                <img src={photoGalleryIcon} alt="" />
              </button>
            </>
          )}
        </div>
      </div>

      {showIntro && (
        <div
          className="scan-label-intro-backdrop"
          onClick={dismissIntro}
          data-testid="scan-label-intro"
        >
          {/* Bottom sheet (Figma "Tips sheet") — stop backdrop-close when tapping inside */}
          <div className="scan-label-intro-sheet" onClick={(e) => e.stopPropagation()}>
            {/* One shipped illustration instead of two thumbs — it already shows both shots side by
                side, with the good/bad marks and the label copy baked in (hence per-language). The
                numbered captions sit below it, one per half, as in Figma 8838:17597 (label y=162
                against a 150px-tall image). */}
            <img src={tipsImg} alt="" className="scan-label-intro-img" />
            <div className="scan-label-intro-captions">
              <p className="description mb-0">{t('tracker.scanner.intro.imageLabel.front')}</p>
              <p className="description mb-0">{t('tracker.scanner.intro.imageLabel.nutrition')}</p>
            </div>

            <h2 className="title-small bold text-center mt-l mb-xs">
              {t('tracker.scanner.intro.title')}
            </h2>
            <p className="text-text-variant-color text-center mb-l">
              {t('tracker.scanner.intro.description')}
            </p>

            <Button
              color="primary"
              className="w-100"
              onClick={dismissIntro}
              data-testid="scan-label-intro-cta"
            >
              {t('tracker.scanner.intro.cta')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackerScanLabelContainer;
